import type { TDateTime, TLocalDate } from "@/utils/time";

import { brandString } from "@/utils/branded";
import { assertUnreachable } from "@/utils/types-utils";

export enum EDay {
  MONDAY = "1",
  TUESDAY = "2",
  WEDNESDAY = "3",
  THURSDAY = "4",
  FRIDAY = "5",
  SATURDAY = "6",
  SUNDAY = "7",
}

export const dayMapping = {
  0: EDay.MONDAY,
  1: EDay.TUESDAY,
  2: EDay.WEDNESDAY,
  3: EDay.THURSDAY,
  4: EDay.FRIDAY,
  5: EDay.SATURDAY,
  6: EDay.SUNDAY,
} as const satisfies Record<0 | 1 | 2 | 3 | 4 | 5 | 6, EDay>;

export enum EDateScaling {
  YEAR = "year",
  YEARS = "years",
  QUARTER = "quarter",
  QUARTERS = "quarters",
  MONTH = "month",
  MONTHS = "months",
  WEEK = "week",
  DAY = "day",
  DAYS = "days",
  HOUR = "hour",
  HOURS = "hours",
  MINUTE = "minute",
  MINUTES = "minutes",
  SECOND = "second",
  SECONDS = "seconds",
  MILLISECOND = "millisecond",
  MILLISECONDS = "milliseconds",
}

export const QUARTERS_BY_MONTHS: Record<number, number> = {
  0: 0,
  1: 0,
  2: 0,
  3: 1,
  4: 1,
  5: 1,
  6: 2,
  7: 2,
  8: 2,
  9: 3,
  10: 3,
  11: 3,
};

export const START_MONTH_BY_QUARTER: Record<number, number> = {
  0: 0,
  1: 3,
  2: 6,
  3: 9,
};

export const END_MONTH_BY_QUARTER: Record<number, number> = {
  0: 2,
  1: 5,
  2: 8,
  3: 11,
};

const $date = {
  formatDateWithoutTimezone(date: Date): TDateTime {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return brandString<TDateTime>(`${year}-${month}-${day}T${hours}:${minutes}:${seconds}`);
  },

  formatLocalDate(date: Date): TLocalDate {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return brandString<TLocalDate>(`${year}-${month}-${day}`);
  },

  getQuarter(date: Date) {
    if (date.getMonth() <= 2) {
      // Jan -> March, jan = 0
      return 0;
    }

    if (date.getMonth() <= 5) {
      return 1;
    }

    if (date.getMonth() <= 8) {
      return 2;
    }

    return 3;
  },

  add(date: Date, amount: number, scale: EDateScaling) {
    amount = Math.floor(amount);

    let year = date.getFullYear();
    let month = date.getMonth();
    let day = date.getDate();
    let hours = date.getHours();
    let minutes = date.getMinutes();
    let seconds = date.getSeconds();
    let milliseconds = date.getMilliseconds();

    switch (scale) {
      case EDateScaling.YEAR:
      case EDateScaling.YEARS:
        year += amount;
        break;
      case EDateScaling.QUARTER:
      case EDateScaling.QUARTERS:
        // Ajout du nombre de trimestres (3 mois par trimestre)
        month += amount * 3;
        break;
      case EDateScaling.MONTH:
      case EDateScaling.MONTHS:
        month += amount;

        // if day > new month last day, set new day to that value
        const newMonthLastDay = this.getLastDayOfMonth(year, month);
        if (day > newMonthLastDay) {
          day = newMonthLastDay;
        }
        break;
      case EDateScaling.WEEK:
        day += amount * 7;

        // if day
        break;
      case EDateScaling.DAY:
      case EDateScaling.DAYS:
        day += amount;
        break;
      case EDateScaling.HOUR:
      case EDateScaling.HOURS:
        hours += amount;
        break;
      case EDateScaling.MINUTE:
      case EDateScaling.MINUTES:
        minutes += amount;
        break;
      case EDateScaling.SECOND:
      case EDateScaling.SECONDS:
        seconds += amount;
        break;
      case EDateScaling.MILLISECOND:
      case EDateScaling.MILLISECONDS:
        milliseconds += amount;
        break;
      default:
        return assertUnreachable(scale);
    }

    // Normalisation des valeurs (les mois, jours, heures, etc. peuvent dépasser leurs limites)
    return new Date(year, month, day, hours, minutes, seconds, milliseconds);
  },

  diff(date_a: Date, date_b: Date, scale: EDateScaling = EDateScaling.DAY) {
    const milliseconds = date_a.valueOf() - date_b.valueOf();
    const seconds = milliseconds / 1000;
    const minutes = seconds / 60;
    const hours = minutes / 60;
    const days = hours / 24;
    const weeks = days / 7;

    const date_a_utc = new Date(Date.UTC(date_a.getFullYear(), date_a.getMonth(), date_a.getDate()));
    const date_b_utc = new Date(Date.UTC(date_b.getFullYear(), date_b.getMonth(), date_b.getDate()));

    const years = date_a_utc.getFullYear() - date_b_utc.getFullYear();
    const months = years * 12 + (date_a_utc.getMonth() - date_b_utc.getMonth());

    let quarters = years * 4;
    const aQuarter = QUARTERS_BY_MONTHS[date_a.getMonth()];
    const bQuarter = QUARTERS_BY_MONTHS[date_b.getMonth()];
    quarters += aQuarter - bQuarter;

    const mapping: { [k in EDateScaling]: number } = {
      year: years,
      years: years,
      quarters: quarters,
      quarter: quarters,
      month: months,
      months: months,
      week: weeks,
      day: days,
      days: days,
      hour: hours,
      hours: hours,
      minute: minutes,
      minutes: minutes,
      second: seconds,
      seconds: seconds,
      millisecond: milliseconds,
      milliseconds: milliseconds,
    };

    return Math.ceil(mapping[scale]);
  },

  subtract(date: Date, amount: number, scale: EDateScaling) {
    amount = Math.floor(amount);

    let year = date.getFullYear();
    let month = date.getMonth();
    let day = date.getDate();
    let hours = date.getHours();
    let minutes = date.getMinutes();
    let seconds = date.getSeconds();
    let milliseconds = date.getMilliseconds();

    switch (scale) {
      case EDateScaling.YEAR:
      case EDateScaling.YEARS:
        year -= amount;
        break;
      case EDateScaling.QUARTER:
      case EDateScaling.QUARTERS:
        // Soustraction du nombre de trimestres (3 mois par trimestre)
        month -= amount * 3;
        break;
      case EDateScaling.MONTH:
      case EDateScaling.MONTHS:
        month -= amount;

        // if day > new month last day, set day to that value
        const newMonthLastDay = this.getLastDayOfMonth(year, month);
        if (day > newMonthLastDay) {
          day = newMonthLastDay;
        }
        break;
      case EDateScaling.WEEK:
        day -= amount * 7;
        break;
      case EDateScaling.DAY:
      case EDateScaling.DAYS:
        day -= amount;
        break;
      case EDateScaling.HOUR:
      case EDateScaling.HOURS:
        hours -= amount;
        break;
      case EDateScaling.MINUTE:
      case EDateScaling.MINUTES:
        minutes -= amount;
        break;
      case EDateScaling.SECOND:
      case EDateScaling.SECONDS:
        seconds -= amount;
        break;
      case EDateScaling.MILLISECOND:
      case EDateScaling.MILLISECONDS:
        milliseconds -= amount;
        break;
      default:
        return assertUnreachable(scale);
    }

    // Normalisation des valeurs (les mois, jours, heures, etc. peuvent dépasser leurs limites)
    return new Date(year, month, day, hours, minutes, seconds, milliseconds);
  },

  startOf(date: Date, scale: EDateScaling) {
    const scores: Record<EDateScaling, number> = {
      year: 6,
      years: 6,
      month: 5,
      months: 5,
      quarter: 5,
      quarters: 5,
      week: 4,
      day: 4,
      days: 4,
      hour: 3,
      hours: 3,
      minute: 2,
      minutes: 2,
      second: 1,
      seconds: 1,
      millisecond: 0,
      milliseconds: 0,
    };

    function should_reset(_scale: EDateScaling) {
      const max_score = scores[scale];
      return scores[_scale] <= max_score;
    }

    let year = date.getFullYear();
    let month = date.getMonth();

    if (scale === EDateScaling.QUARTER || scale === EDateScaling.QUARTERS) {
      const currentQuarter = QUARTERS_BY_MONTHS[month];
      month = START_MONTH_BY_QUARTER[currentQuarter];
    }

    let day = date.getDate();
    if (scale === EDateScaling.WEEK) {
      const weekDay = this.getDateWeekDay(date);
      if (weekDay !== 0) {
        // date day is not monday (not start of week)
        const startOfWeek = this.subtract(date, weekDay, EDateScaling.DAY);
        year = startOfWeek.getFullYear();
        month = startOfWeek.getMonth();
        day = startOfWeek.getDate();
      }
    }

    const [yy, mm, dd, hh, mm2, ss, ms] = [
      year,
      should_reset(EDateScaling.YEAR) ? 0 : month, // Début du trimestre ou mois donné
      should_reset(EDateScaling.MONTH) ? 1 : day, // Premier jour du mois, trimestre ou semaine
      should_reset(EDateScaling.DAY) ? 0 : date.getHours(), // Première heure du jour
      should_reset(EDateScaling.HOUR) ? 0 : date.getMinutes(), // Première minute de l'heure
      should_reset(EDateScaling.MINUTE) ? 0 : date.getSeconds(), // Première seconde de la minute
      should_reset(EDateScaling.SECOND) ? 0 : date.getMilliseconds(), // Première milliseconde de la seconde
    ] as const;

    return new Date(yy, mm, dd, hh, mm2, ss, ms);
  },

  endOf(date: Date, scale: EDateScaling) {
    const scores: Record<EDateScaling, number> = {
      year: 7,
      years: 7,
      month: 6,
      months: 6,
      quarter: 5,
      quarters: 5,
      week: 4,
      day: 4,
      days: 4,
      hour: 3,
      hours: 3,
      minute: 2,
      minutes: 2,
      second: 1,
      seconds: 1,
      millisecond: 0,
      milliseconds: 0,
    };

    function should_set_to_max(_scale: EDateScaling) {
      const max_score = scores[scale];
      return scores[_scale] <= max_score;
    }

    let year = date.getFullYear();
    let month = date.getMonth();
    let day = date.getDate();
    if (scale === EDateScaling.QUARTER || scale === EDateScaling.QUARTERS) {
      const currentQuarter = QUARTERS_BY_MONTHS[month];
      month = END_MONTH_BY_QUARTER[currentQuarter];
      day = this.getLastDayOfMonth(date.getFullYear(), month);
    }

    if (scale === EDateScaling.WEEK) {
      const weekDay = this.getDateWeekDay(date);
      if (weekDay !== 6) {
        // date day is not sunday (not end of week)
        const endOfWeek = this.add(date, 6 - weekDay, EDateScaling.DAY);
        year = endOfWeek.getFullYear();
        month = endOfWeek.getMonth();
        day = endOfWeek.getDate();
      }
    }

    const [yy, mm, dd, hh, mm2, ss, ms] = [
      year,
      should_set_to_max(EDateScaling.YEAR) ? 11 : month, // mois maximum = décembre
      should_set_to_max(EDateScaling.MONTH) ? this.getLastDayOfMonth(date.getFullYear(), date.getMonth()) : day, // dernier jour du mois ou semaine
      should_set_to_max(EDateScaling.DAY) ? 23 : date.getHours(), // dernière heure = 23h
      should_set_to_max(EDateScaling.HOUR) ? 59 : date.getMinutes(), // dernière minute = 59
      should_set_to_max(EDateScaling.MINUTE) ? 59 : date.getSeconds(), // dernière seconde = 59
      should_set_to_max(EDateScaling.SECOND) ? 999 : date.getMilliseconds(), // dernier milliseconde = 999
    ] as const;

    return new Date(yy, mm, dd, hh, mm2, ss, ms);
  },

  clone(date: Date) {
    const vals = this.getDateValues(date);
    return new Date(vals[0], vals[1], vals[2], vals[3], vals[4], vals[5], vals[6]);
  },

  daysInMonth(date: Date) {
    const no_of_days = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    const month = date.getMonth();

    if (month !== 1) {
      return no_of_days[month];
    }

    const year = date.getFullYear();
    if ((year % 4 === 0 && year % 100 != 0) || year % 400 === 0) {
      return 29;
    }
    return 28;
  },

  getDateValues(date: Date) {
    return [
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds(),
    ];
  },

  daysInQuarter(date: Date) {
    let nb = 0;

    const quarter = QUARTERS_BY_MONTHS[date.getMonth()];
    const start = START_MONTH_BY_QUARTER[quarter];
    const end = END_MONTH_BY_QUARTER[quarter];

    for (let i = start; i <= end; i++) {
      const tmpDate = new Date(date.getFullYear(), i, 0);
      nb += this.daysInMonth(tmpDate);
    }

    return nb;
  },

  getDateWeekDay(date: TLocalDate | Date): 0 | 1 | 2 | 3 | 4 | 5 | 6 {
    return ((new Date(date).getDay() + 6) % 7) as 0 | 1 | 2 | 3 | 4 | 5 | 6; // make monday = 0 and sunday = 6
  },

  getDateMonth(date: TLocalDate | Date): number {
    return new Date(date).getMonth() + 1; // so january = 1
  },

  getDateYear(date: TLocalDate | Date): number {
    return new Date(date).getFullYear();
  },

  getLastDayOfMonth(fullYear: number, month: number): number {
    return new Date(fullYear, month + 1, 0).getDate();
  },

  getDateMilliseconds(date: TDateTime | Date): number {
    return new Date(date).getTime();
  },

  getISOWeekNumber(date: Date): number {
    // following ISO week dates rules https://en.wikipedia.org/wiki/ISO_week_date
    // first week of the year should contain January 4th
    function getMondayOfWeek1ByYear(year: number) {
      return $date.startOf(new Date(year, 0, 4), EDateScaling.WEEK);
    }

    const mondayOfDateYearWeek1 = getMondayOfWeek1ByYear(date.getFullYear());
    const mondayOfDateFollowingYearWeek1 = getMondayOfWeek1ByYear(date.getFullYear() + 1);
    const mondayOfDate = this.startOf(date, EDateScaling.WEEK);

    if (this.getDateMilliseconds(mondayOfDate) === this.getDateMilliseconds(mondayOfDateFollowingYearWeek1)) {
      // first week of following year
      return 1;
    }

    const yearOfMondayOfDateYearWeek1 = mondayOfDateYearWeek1.getFullYear();
    const yearOfMondayOfDate = mondayOfDate.getFullYear();

    if (yearOfMondayOfDate !== yearOfMondayOfDateYearWeek1) {
      const mondayOfWeek1 = getMondayOfWeek1ByYear(yearOfMondayOfDate);
      return this.diff(mondayOfDate, mondayOfWeek1, EDateScaling.WEEK) + 1;
    }

    return this.diff(mondayOfDate, mondayOfDateYearWeek1, EDateScaling.WEEK) + 1;
  },

  updateDateOnly(updatedDate: Date | undefined, newDate: Date) {
    if (!updatedDate) return;

    updatedDate.setDate(newDate.getDate());
    updatedDate.setMonth(newDate.getMonth());
    updatedDate.setFullYear(newDate.getFullYear());
  },
};

export default $date;
